/**
 * <home-banner-slider>
 * Homepage hero carousel using native CSS flex + transform translateX.
 * No user touch (autoplay-only — matches original allowTouchMove: false).
 * - Slides filtered per viewport and rebuilt when the breakpoint changes
 * - Seamless forward loop via cloned first slide at end (no visible jump)
 * - Pagination dots auto-built from slide count
 * - Video play/pause coordinated with active slide
 * - GIF detection pauses autoplay
 *
 * Included by both home-banner-slider.liquid and common-slider.liquid, so it
 * executes twice on pages rendering both sections. The guard makes the second
 * execution a no-op — a top-level class declaration would otherwise throw
 * "Identifier has already been declared".
 */
if (!window.customElements.get("home-banner-slider")) {
  class HomeBannerSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector(".banner-track");
      if (!this.track) return;

      this.allSlides ??= Array.from(this.track.querySelectorAll(".banner-slide:not(.is-clone)"));
      this.autoplayEnabled = this.dataset.autoplay !== "false";
      this.autoplayDelay = parseInt(this.dataset.autoplayDelay, 10) || 3000;
      // Match the desktop visibility rules in global.css, including tablets
      // in landscape; otherwise hidden mobile slides still count for autoplay.
      this.viewport = window.matchMedia("(min-width: 1025px), (min-width: 768px) and (orientation: landscape)");
      this.onViewportChange = () => this.refreshSlides();
      this.viewport.addEventListener("change", this.onViewportChange);
      this.refreshSlides();
    }

    refreshSlides() {
      this.stopAutoplay();
      this.cancelCloneSnap();
      this.controller?.abort();
      this.controller = new AbortController();
      this.allSlides.forEach((slide) => {
        slide.querySelectorAll("video.banner-video").forEach((video) => video.pause());
        slide.classList.remove("is-active");
      });
      const excludedClass = this.viewport.matches ? "mobile-only" : "desktop-only";
      this.slides = this.allSlides.filter((slide) => !slide.classList.contains(excludedClass));
      this.track.replaceChildren(...this.slides);
      this.cloneSlide = null;
      this.currentIndex = 0;
      this.track.style.transition = "none";
      this.track.style.transform = "translateX(0)";
      void this.track.offsetWidth;
      if (this.slides.length === 0) return;

      this.setupVideos();
      this.buildClone();
      this.setActive(0);
      void this.track.offsetWidth;
      if (this.slides.length > 1) this.track.style.transition = "";
      this.handleVideoPlayback();

      if (this.slides.length > 1) {
        this.checkGifAndPauseAutoplay();
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.cancelCloneSnap();
      this.controller?.abort();
      this.viewport?.removeEventListener("change", this.onViewportChange);
    }

    setupVideos() {
      const videos = this.querySelectorAll("video.banner-video");
      const isWide = window.innerWidth > 1600;
      const isDesktop = this.viewport.matches;

      videos.forEach((video) => {
        const widePoster = video.dataset.posterWide;
        const desktopPoster = video.dataset.posterDesktop;
        const mobilePoster = video.dataset.posterMobile;

        if (isWide && widePoster) {
          video.setAttribute("poster", widePoster);
        } else if (isDesktop && desktopPoster) {
          video.setAttribute("poster", desktopPoster);
        } else if (mobilePoster) {
          video.setAttribute("poster", mobilePoster);
        }

        const rawSpeed = isDesktop ? video.dataset.playbackSpeedDesktop : video.dataset.playbackSpeedMobile;
        const pct = parseFloat(rawSpeed);
        const rate = isFinite(pct) && pct > 0 ? pct / 100 : 1;
        video.playbackRate = rate;
        video.addEventListener(
          "loadedmetadata",
          () => {
            video.playbackRate = rate;
          },
          { signal: this.controller.signal }
        );
      });
    }

    buildClone() {
      // Append a clone of slide 0 after the last slide. When autoplay advances
      // past the last real slide, it lands on the clone (which looks like slide 0).
      // Once the transition completes we instant-snap back to real slide 0 —
      // visually seamless, no backward sweep across all slides.
      if (this.slides.length <= 1) return;
      const clone = this.slides[0].cloneNode(true);
      clone.classList.add("is-clone");
      // Don't double-play videos on the clone; original slide 0 owns playback.
      clone.querySelectorAll("video").forEach((v) => {
        v.removeAttribute("autoplay");
        v.muted = true;
      });
      this.track.appendChild(clone);
      this.cloneSlide = clone;
    }

    setActive(index) {
      this.currentIndex = index;
      this.track.style.transform = `translateX(${-index * 100}%)`;
      // Map clone position back to real slide 0 for active state.
      const realIndex = this.realIndexOf(index);
      this.slides.forEach((slide, i) => {
        slide.classList.toggle("is-active", i === realIndex);
      });
    }

    realIndexOf(index) {
      return index >= this.slides.length ? 0 : index;
    }

    goTo(index) {
      if (index < 0 || index >= this.slides.length) return;
      if (index === this.currentIndex) return;
      this.cancelCloneSnap();
      // Re-enable transition in case a snap left it disabled.
      this.track.style.transition = "";
      this.setActive(index);
      this.handleVideoPlayback();
      this.checkGifAndPauseAutoplay();
    }

    next() {
      if (this.slides.length <= 1) return;
      const targetIndex = this.currentIndex + 1;
      this.track.style.transition = "";
      this.setActive(targetIndex);
      this.handleVideoPlayback();
      if (targetIndex >= this.slides.length) {
        // We just animated onto the clone — schedule instant snap back to 0.
        this.scheduleCloneSnap();
      } else {
        this.checkGifAndPauseAutoplay();
      }
    }

    scheduleCloneSnap() {
      this.cancelCloneSnap();
      this.cloneSnapHandler = () => {
        this.cloneSnapHandler = null;
        this.track.style.transition = "none";
        this.currentIndex = 0;
        this.track.style.transform = "translateX(0)";
        // Force reflow so the next style change re-enables the transition.
        void this.track.offsetWidth;
        this.track.style.transition = "";
        this.checkGifAndPauseAutoplay();
      };
      this.track.addEventListener("transitionend", this.cloneSnapHandler, {
        once: true,
        signal: this.controller.signal,
      });
    }

    cancelCloneSnap() {
      if (this.cloneSnapHandler) {
        this.track.removeEventListener("transitionend", this.cloneSnapHandler);
        this.cloneSnapHandler = null;
      }
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.slides.length <= 1 || this.intervalId) return;
      this.intervalId = setInterval(() => this.next(), this.autoplayDelay);
    }

    stopAutoplay() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    handleVideoPlayback() {
      const realIndex = this.realIndexOf(this.currentIndex);
      this.slides.forEach((slide, i) => {
        slide.querySelectorAll("video.banner-video").forEach((v) => {
          if (i === realIndex) {
            v.play().catch(() => {});
          } else {
            v.pause();
          }
        });
      });
    }

    checkGifAndPauseAutoplay() {
      const realIndex = this.realIndexOf(this.currentIndex);
      const activeSlide = this.slides[realIndex];
      if (activeSlide.querySelector('img[src$=".gif"]')) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }
  }

  window.customElements.define("home-banner-slider", HomeBannerSlider);

  // Mobile button: click takes user to active slide's link.
  document.addEventListener("DOMContentLoaded", () => {
    const bannerButton = document.querySelector(".home-slider-mobile-button");
    if (!bannerButton) return;
    bannerButton.addEventListener("click", () => {
      const activeSlide = document.querySelector(".banner-slide.is-active");
      const link = activeSlide?.querySelector("a[href]");
      const href = link?.getAttribute("href");
      if (href) {
        window.location.href = href;
      }
    });
  });
}
